import React, { useState, useMemo } from 'react';
import { useReportActivities } from '../hooks/useActivities';
import { Search, Calendar, Filter, Users, ChevronLeft, ChevronRight, FileText } from 'lucide-react';

function uniqueActions(activities) {
  return Array.from(new Set(activities.map(a => a.action))).filter(Boolean);
}

function uniquePerformers(activities) {
  return Array.from(new Set(activities.map(a => a.performed_by))).filter(Boolean);
}

export default function ReportActivitiesPage() {
  const { activities, loading, error } = useReportActivities();
  const [search, setSearch] = useState('');
  const [action, setAction] = useState('');
  const [performedBy, setPerformedBy] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 10;

  // Debug logging
  console.log('ReportActivitiesPage - activities:', activities);
  console.log('ReportActivitiesPage - loading:', loading);
  console.log('ReportActivitiesPage - error:', error);

  const filtered = useMemo(() => {
    // Ensure activities is an array
    if (!Array.isArray(activities)) {
      console.log('Activities is not an array:', activities);
      return [];
    }
    console.log('Filtering activities, count:', activities.length);
    const result = activities.filter(act => {
      const searchText = `${act.report_title || ''} ${act.description || ''}`.toLowerCase();
      const matchesSearch = searchText.includes(search.toLowerCase());
      const matchesAction = action ? act.action === action : true;
      const matchesPerformer = performedBy ? act.performed_by === performedBy : true;
      const actDate = new Date(act.timestamp);
      const matchesFrom = dateFrom ? actDate >= new Date(dateFrom) : true;
      const matchesTo = dateTo ? actDate <= new Date(dateTo + 'T23:59:59') : true;
      return matchesSearch && matchesAction && matchesPerformer && matchesFrom && matchesTo;
    });
    console.log('Filtered results count:', result.length);
    return result;
  }, [activities, search, action, performedBy, dateFrom, dateTo]);

  const paginated = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, page]);

  const totalPages = Math.ceil(filtered.length / pageSize) || 1;
  const actions = useMemo(() => Array.isArray(activities) ? uniqueActions(activities) : [], [activities]);
  const performers = useMemo(() => Array.isArray(activities) ? uniquePerformers(activities) : [], [activities]);

  const handleReset = () => {
    setSearch('');
    setAction('');
    setPerformedBy('');
    setDateFrom('');
    setDateTo('');
    setPage(1);
  };

  return (
    <div className="max-w-6xl mx-auto p-6 bg-gray-50 min-h-screen">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-800">Report Activities</h1>
        <p className="text-gray-500 mt-2">View and track all activities on your reports</p>
      </div>
      
      <div className="bg-white rounded-xl shadow-md p-6 mb-6">
        <div className="flex flex-col space-y-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-grow">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search size={18} className="text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Search title or description..."
                className="pl-10 w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                value={search}
                onChange={e => { setSearch(e.target.value); setPage(1); }}
              />
            </div>
          </div>
          
          <div className="flex flex-wrap gap-3">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Filter size={18} className="text-gray-400" />
              </div>
              <select
                className="pl-10 border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none bg-white"
                value={action}
                onChange={e => { setAction(e.target.value); setPage(1); }}
              >
                <option value="">All Actions</option>
                {actions.map(a => <option key={a} value={a}>{a}</option>)}
              </select>
            </div>
            
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Users size={18} className="text-gray-400" />
              </div>
              <select
                className="pl-10 border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none bg-white"
                value={performedBy}
                onChange={e => { setPerformedBy(e.target.value); setPage(1); }}
              >
                <option value="">All Users</option>
                {performers.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Calendar size={18} className="text-gray-400" />
              </div>
              <input
                type="date"
                placeholder="From"
                className="pl-10 border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                value={dateFrom}
                onChange={e => { setDateFrom(e.target.value); setPage(1); }}
              />
            </div>
            
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Calendar size={18} className="text-gray-400" />
              </div>
              <input
                type="date"
                placeholder="To"
                className="pl-10 border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                value={dateTo}
                onChange={e => { setDateTo(e.target.value); setPage(1); }}
              />
            </div>
            
            <button 
              onClick={handleReset}
              className="px-4 py-2 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition duration-200"
            >
              Reset
            </button>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto"></div>
            <p className="mt-4 text-gray-500">Loading report activities...</p>
          </div>
        ) : error ? (
          <div className="text-center text-red-500 py-16 bg-red-50 rounded-lg">
            <p className="font-medium">Error loading report activities</p>
            <p className="text-sm mt-2">{error}</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 bg-gray-50 rounded-lg">
            <p className="text-gray-400 font-medium">No report activities found matching your criteria</p>
            <button 
              onClick={handleReset}
              className="mt-4 px-4 py-2 text-blue-600 border border-blue-600 rounded-lg hover:bg-blue-50"
            >
              Clear filters
            </button>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto rounded-lg border border-gray-200">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Report Type</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Report ID</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Performed By</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Time</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {paginated.map((act) => (
                    <tr key={act.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm font-mono text-gray-600">{act.id}</td>
                      <td className="px-4 py-3 text-sm">
                        <div className="flex items-center">
                          <FileText size={16} className="mr-2 text-gray-400" />
                          {act.object_type}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm font-mono">{act.object_id}</td>
                      <td className="px-4 py-3 text-sm font-medium">{act.report_title}</td>
                      <td className="px-4 py-3 text-sm">
                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-purple-100 text-purple-800">
                          {act.performed_by}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                          {act.action}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">{new Date(act.timestamp).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            <div className="flex flex-col sm:flex-row justify-between items-center mt-6 gap-4">
              <div className="text-sm text-gray-600">
                Showing {filtered.length > 0 ? (page - 1) * pageSize + 1 : 0} - {Math.min(page * pageSize, filtered.length)} of {filtered.length} report activities
              </div>
              
              <div className="flex items-center gap-2">
                <button 
                  disabled={page === 1} 
                  onClick={() => setPage(p => p - 1)} 
                  className="flex items-center justify-center w-10 h-10 rounded bg-white border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  <ChevronLeft size={18} />
                </button>
                <div className="text-sm font-medium">
                  Page {page} of {totalPages}
                </div>
                <button 
                  disabled={page === totalPages} 
                  onClick={() => setPage(p => p + 1)} 
                  className="flex items-center justify-center w-10 h-10 rounded bg-white border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  <ChevronRight size={18} />
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}